<template>
    <div class="ei-root">
        <EnIntMasterHeader
            :date-type="dateType"
            :date-value="dateValue"
            :year-picker-model.sync="yearPickerModel"
            :month-picker-model.sync="monthPickerModel"
            @set-date-type="setDateType"
            @year-change="onYearPickerChange"
            @month-change="onMonthPickerChange"
            @quick-shift="onQuickShift"
        />

        <div class="ei-body">
            <EnIntListPanel
                ref="listPanel"
                :selected-en-int-idx="selectedEnIntIdx"
                @selection-change="onListSelectionChange"
                @edit="onEdit"
                @deleted="onListDeleted"
                @new="showNew = true"
            />

            <EnIntBridgePanel
                :en-int-idx="selectedEnIntIdx"
                :reload-token="bridgeReloadToken"
                :date-type="dateType"
                :date-value="dateValue"
                :master-api-state="sharedMasterApiState"
                :shared-calc-list-api-state="sharedCalcListApiState"
                @edit="onEdit"
            />

            <section class="ei-detail-area">
                <div v-if="!selectedEnIntIdx" class="ei-detail-empty">
                    <i class="fas fa-hand-pointer"></i>
                    <span>좌측 목록에서 항목을 선택하세요</span>
                </div>
                <EnIntMasterDetailPanel
                    v-else
                    :key="detailKey"
                    :en-int-idx="selectedEnIntIdx"
                    :date-type="dateType"
                    :date-value="dateValue"
                    :master-api-state="sharedMasterApiState"
                    :shared-calc-list-api-state="sharedCalcListApiState"
                    @data-saved="bumpBridge"
                />
            </section>
        </div>

        <b-modal v-model="showNew" size="lg" centered scrollable hide-footer title="원단위 신규 등록" body-class="p-0">
            <EnIntMasterNewModal @saved="onNewSaved" @cancel="showNew = false" />
        </b-modal>

        <b-modal v-model="showEdit" size="lg" centered scrollable hide-footer title="원단위 수정" body-class="p-0">
            <EnIntEditModal
                v-if="editEnIntIdx"
                :en-int-idx="editEnIntIdx"
                :date-type="dateType"
                :date-value="dateValue"
                @saved="onEditSaved"
                @cancel="showEdit = false"
            />
        </b-modal>
    </div>
</template>

<script>
import backEndApi from "@src/api/backEndApi";
import {
    currentYm,
    ENINT_DATE_TYPE_YEAR,
    ENINT_DATE_TYPE_MONTH,
    ymFromMonthInput,
    ymAddMonths,
    enIntMasterCalcListUnion,
    isValidDatePeriod,
} from "./utils/enIntUi.js";
import EnIntMasterHeader from "./components/EnIntMasterHeader.vue";
import EnIntListPanel from "./components/EnIntListPanel.vue";
import EnIntBridgePanel from "./components/EnIntBridgePanel.vue";
import EnIntMasterDetailPanel from "./components/EnIntMasterDetailPanel.vue";
import EnIntMasterNewModal from "./components/EnIntMasterNewModal.vue";
import EnIntEditModal from "./components/EnIntEditModal.vue";

function toMonthStr(ym) {
    const s = String(ym);
    if (s.length !== 6) return "";
    return `${s.slice(0, 4)}-${s.slice(4, 6)}`;
}

export default {
    name: "EnIntMasterMain",
    components: {
        EnIntMasterHeader,
        EnIntListPanel,
        EnIntBridgePanel,
        EnIntMasterDetailPanel,
        EnIntMasterNewModal,
        EnIntEditModal,
    },
    data() {
        const y = new Date().getFullYear();
        return {
            ENINT_DATE_TYPE_YEAR,
            ENINT_DATE_TYPE_MONTH,
            selectedEnIntIdx: null,
            detailKey: 0,
            bridgeReloadToken: 0,
            showNew: false,
            showEdit: false,
            editEnIntIdx: null,
            sharedMasterApiState: {
                response: null,
                data: null,
                loading: false,
                error: "",
            },
            sharedMasterFetchGen: 0,
            sharedCalcListApiState: {
                response: null,
                data: null,
                loading: false,
                error: "",
            },
            sharedCalcListFetchGen: 0,
            dateType: ENINT_DATE_TYPE_YEAR,
            dateValue: y,
            yearPickerModel: String(y),
            monthPickerModel: toMonthStr(currentYm()),
        };
    },
    computed: {
        /** 선택·브리지 갱신 토큰이 바뀔 때마다 한 번만 마스터 재조회 */
        sharedMasterFetchKey() {
            return `${this.selectedEnIntIdx ?? ""}:${this.bridgeReloadToken}`;
        },
        /**
         * getCalcList 실제 파라미터(enIntIdx, unionFrom, unionTo) + 저장 후 갱신(bridgeReloadToken)만 반영.
         * 같은 연·같은 union 구간에서 월(MONTH)만 바꾸면 키가 동일 → 불필요한 재호출 없음.
         */
        sharedCalcListFetchKey() {
            const idx = this.selectedEnIntIdx;
            if (!idx || !isValidDatePeriod(this.dateType, this.dateValue)) {
                return "";
            }
            const union = enIntMasterCalcListUnion(this.dateType, this.dateValue);
            if (!union) return "";
            return `${idx}|${union.unionFrom}|${union.unionTo}|${this.bridgeReloadToken}`;
        },
    },
    watch: {
        sharedMasterFetchKey: {
            immediate: true,
            handler() {
                this.loadSharedMaster();
            },
        },
        sharedCalcListFetchKey: {
            immediate: true,
            handler() {
                this.loadSharedCalcList();
            },
        },
        dateType(t) {
            if (t === ENINT_DATE_TYPE_MONTH) {
                this.monthPickerModel = toMonthStr(this.dateValue > 200000 ? this.dateValue : currentYm());
                const ym = ymFromMonthInput(this.monthPickerModel);
                if (ym) this.dateValue = ym;
            } else if (t === ENINT_DATE_TYPE_YEAR) {
                const yy =
                    Number(this.dateValue) > 200000 ? Math.floor(Number(this.dateValue) / 100) : Number(this.dateValue);
                if (Number.isFinite(yy) && yy >= 2000 && yy <= 2100) {
                    this.yearPickerModel = String(yy);
                    this.dateValue = yy;
                }
            }
        },
        dateValue(v) {
            if (this.dateType === ENINT_DATE_TYPE_MONTH && v > 200000) {
                const next = toMonthStr(v);
                if (next && next !== this.monthPickerModel) this.monthPickerModel = next;
            } else if (this.dateType === ENINT_DATE_TYPE_YEAR) {
                const n = Number(v);
                if (n >= 2000 && n <= 2100 && String(n) !== this.yearPickerModel) {
                    this.yearPickerModel = String(n);
                }
            }
        },
    },
    methods: {
        setDateType(t) {
            if (t === this.dateType) return;
            const prevType = this.dateType;
            const prevVal = Number(this.dateValue);
            if (t === ENINT_DATE_TYPE_YEAR) {
                const y = prevType === ENINT_DATE_TYPE_MONTH && prevVal > 200000 ? Math.floor(prevVal / 100) : prevVal;
                this.dateType = t;
                const nextY = Number.isFinite(y) && y >= 2000 && y <= 2100 ? y : new Date().getFullYear();
                this.dateValue = nextY;
                this.yearPickerModel = String(nextY);
            } else {
                this.dateType = ENINT_DATE_TYPE_MONTH;
                let ym;
                if (prevType === ENINT_DATE_TYPE_YEAR && prevVal >= 2000 && prevVal <= 2100) {
                    ym = prevVal * 100 + 1;
                } else if (prevVal > 200000) {
                    ym = prevVal;
                } else {
                    ym = currentYm();
                }
                this.dateValue = ym;
                this.monthPickerModel = toMonthStr(ym);
            }
        },
        onYearPickerChange() {
            const y = parseInt(String(this.yearPickerModel || ""), 10);
            if (Number.isFinite(y) && y >= 2000 && y <= 2100) this.dateValue = y;
        },
        onMonthPickerChange() {
            const ym = ymFromMonthInput(this.monthPickerModel);
            if (ym) this.dateValue = ym;
        },
        onQuickShift(delta) {
            const d = Number(delta);
            if (!Number.isFinite(d) || (d !== -1 && d !== 1)) return;
            if (this.dateType === ENINT_DATE_TYPE_YEAR) {
                let y = Number(this.dateValue);
                if (this.dateValue > 200000) y = Math.floor(Number(this.dateValue) / 100);
                const ny = y + d;
                if (ny >= 2000 && ny <= 2100) {
                    this.dateValue = ny;
                    this.yearPickerModel = String(ny);
                }
            } else {
                let ym = Number(this.dateValue);
                if (!Number.isFinite(ym) || ym <= 200000) ym = currentYm();
                const ny = ymAddMonths(ym, d);
                const yy = Math.floor(ny / 100);
                const mm = ny % 100;
                if (yy >= 2000 && yy <= 2100 && mm >= 1 && mm <= 12) {
                    this.dateValue = ny;
                    this.monthPickerModel = toMonthStr(ny);
                }
            }
        },
        bumpBridge() {
            this.bridgeReloadToken += 1;
        },
        async loadSharedCalcList() {
            const idx = this.selectedEnIntIdx;
            const union = enIntMasterCalcListUnion(this.dateType, this.dateValue);
            if (!idx || !union) {
                this.sharedCalcListApiState = {
                    response: null,
                    data: null,
                    loading: false,
                    error: "",
                };
                return;
            }
            const gen = (this.sharedCalcListFetchGen += 1);
            this.sharedCalcListApiState = {
                response: null,
                data: null,
                loading: true,
                error: "",
            };
            try {
                const res = await backEndApi.enInt.getCalcList({
                    enIntIdx: idx,
                    fromYm: union.unionFrom,
                    toYm: union.unionTo,
                });
                if (gen !== this.sharedCalcListFetchGen) return;
                const ok = res?.status === 200;
                const body = res?.data ?? null;
                this.sharedCalcListApiState = {
                    response: res,
                    data: ok ? body : null,
                    loading: false,
                    error: ok ? "" : "계산 목록을 불러오지 못했습니다.",
                };
            } catch {
                if (gen !== this.sharedCalcListFetchGen) return;
                this.sharedCalcListApiState = {
                    response: null,
                    data: null,
                    loading: false,
                    error: "계산 목록을 불러오지 못했습니다.",
                };
            }
        },
        async loadSharedMaster() {
            const idx = this.selectedEnIntIdx;
            if (!idx) {
                this.sharedMasterApiState = {
                    response: null,
                    data: null,
                    loading: false,
                    error: "",
                };
                return;
            }
            const gen = (this.sharedMasterFetchGen += 1);
            this.sharedMasterApiState = {
                ...this.sharedMasterApiState,
                loading: true,
                error: "",
            };
            try {
                const getMaster_response = await backEndApi.enInt.getMaster({ enIntIdx: idx });
                const getMaster_data = getMaster_response?.data ?? null;
                if (gen !== this.sharedMasterFetchGen) return;
                if (getMaster_response.status !== 200 || !getMaster_data) throw new Error("master");
                this.sharedMasterApiState = {
                    response: getMaster_response,
                    data: getMaster_data,
                    loading: false,
                    error: "",
                };
            } catch {
                if (gen !== this.sharedMasterFetchGen) return;
                this.sharedMasterApiState = {
                    response: null,
                    data: null,
                    loading: false,
                    error: "마스터 정보를 불러오지 못했습니다.",
                };
            }
        },
        onListSelectionChange(payload) {
            const enIntIdx = payload && typeof payload === "object" ? payload.enIntIdx : payload;
            this.selectedEnIntIdx = enIntIdx != null ? Number(enIntIdx) : null;
        },
        onEdit(enIntIdx) {
            this.editEnIntIdx = enIntIdx;
            this.showEdit = true;
        },
        async onNewSaved(enIntIdx) {
            this.showNew = false;
            await this.$refs.listPanel?.refresh();
            this.selectedEnIntIdx = Number(enIntIdx) || null;
            this.detailKey += 1;
            this.bumpBridge();
        },
        async onEditSaved() {
            this.showEdit = false;
            this.editEnIntIdx = null;
            await this.$refs.listPanel?.refresh();
            this.detailKey += 1;
            this.bumpBridge();
        },
        onListDeleted(enIntIdx) {
            if (this.selectedEnIntIdx === enIntIdx) {
                this.selectedEnIntIdx = null;
                this.detailKey += 1;
            }
            if (this.editEnIntIdx === enIntIdx) {
                this.showEdit = false;
                this.editEnIntIdx = null;
            }
        },
    },
};
</script>

<style scoped>
.ei-root {
    --ei-navy: #0a3d5c;
    --ei-teal: #0e7490;
    --ei-border: rgba(203, 213, 225, 0.84);
    /* 좌·중 열 비율(나머지는 상세 영역 1fr) */
    --ei-list-pct: 18%;
    --ei-bridge-pct: 30%;

    display: flex;
    flex-direction: column;
    height: 91vh;
    min-height: 640px;
    min-width: 0;
    border-radius: 10px;
    overflow: hidden;
    background:
        radial-gradient(circle at top left, rgba(14, 116, 144, 0.1), transparent 28%),
        linear-gradient(180deg, #f8fbff 0%, #f1f5fa 50%, #ecf0f6 100%);
    box-shadow: 0 18px 48px rgba(15, 23, 42, 0.08);
}

.ei-body {
    flex: 1;
    min-height: 0;
    display: grid;
    grid-template-columns: minmax(0, var(--ei-list-pct)) minmax(0, var(--ei-bridge-pct)) minmax(0, 1fr);
    gap: 0;
}

.ei-detail-area {
    min-height: 0;
    min-width: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    padding: 0;
}

.ei-detail-empty {
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;
    background: rgba(255, 255, 255, 0.5);
    color: #94a3b8;
    font-size: 13px;
    font-weight: 600;
}

.ei-detail-empty i {
    font-size: 18px;
}

@media (max-width: 1320px) {
    .ei-root {
        --ei-list-pct: 22%;
        --ei-bridge-pct: 26%;
    }
}

@media (max-width: 960px) {
    .ei-body {
        grid-template-columns: minmax(0, 1fr);
        grid-auto-rows: auto;
    }
}
</style>
